import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Badge, Spinner, Pagination, Alert, CheckpointViewer } from './shared';

const fmtNum = (n) => {
    if (n == null) return '0';
    return n.toLocaleString();
};

function StatsBar({ stats }) {
    if (!stats) return null;
    const items = [
        { label: 'Threads', value: fmtNum(stats.total_threads), color: 'text-blue-600' },
        { label: 'Checkpoints', value: fmtNum(stats.total_checkpoints), color: 'text-gray-900' },
        { label: 'Blobs', value: fmtNum(stats.total_blobs), color: 'text-purple-600' },
        { label: 'Writes', value: fmtNum(stats.total_writes), color: 'text-amber-600' },
        { label: 'Migration', value: `v${stats.migration_version ?? '-'}`, color: 'text-gray-500' },
    ];
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-4">
            {items.map((it) => (
                <div key={it.label} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <p className="text-xs text-gray-500">{it.label}</p>
                    <p className={`text-lg font-semibold ${it.color}`}>{it.value}</p>
                </div>
            ))}
        </div>
    );
}

function ThreadCard({ thread, isSelected, onClick }) {
    return (
        <div
            onClick={onClick}
            className={`px-3 py-2.5 cursor-pointer border-b border-gray-50 transition-colors ${
                isSelected ? 'bg-gray-100 border-l-2 border-l-gray-900' : 'hover:bg-gray-50'
            }`}
        >
            {thread.session_name ? (
                <>
                    <p className="text-xs font-medium text-gray-900 truncate">{thread.session_name}</p>
                    <p className="text-[10px] font-mono text-gray-400 truncate mt-0.5">{thread.thread_id}</p>
                </>
            ) : (
                <p className="text-xs font-mono font-medium text-gray-900 truncate">{thread.thread_id}</p>
            )}
            {thread.user_id && (
                <p className="text-[10px] text-gray-400 truncate mt-0.5">{thread.user_id}</p>
            )}
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <Badge variant="outline">{thread.checkpoint_count} ckpts</Badge>
                <Badge variant="outline">{thread.write_count} writes</Badge>
                <Badge variant="outline">{thread.blob_count} blobs</Badge>
            </div>
        </div>
    );
}

function ThreadLeftPanel({ selectedThread, onSelectThread }) {
    const [threads, setThreads] = useState({ threads: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [offset, setOffset] = useState(0);
    const limit = 50;

    useEffect(() => {
        setLoading(true);
        adminApi.getCheckpointThreads({
            search: search || undefined,
            limit,
            offset,
        })
            .then(setThreads)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [search, offset]);

    return (
        <div className="flex flex-col h-full">
            <div className="shrink-0 p-3 border-b border-gray-100">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
                    placeholder="Search thread / session..."
                    className="w-full h-8 rounded-md border border-gray-200 px-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
            </div>
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <Spinner text="Loading..." center />
                ) : threads.threads.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">No threads found</p>
                ) : (
                    threads.threads.map((t) => (
                        <ThreadCard
                            key={t.thread_id}
                            thread={t}
                            isSelected={selectedThread?.thread_id === t.thread_id}
                            onClick={() => onSelectThread(t)}
                        />
                    ))
                )}
            </div>
            <div className="shrink-0 p-3 border-t border-gray-100">
                <Pagination total={threads.total} limit={limit} offset={offset} onChange={setOffset} />
            </div>
        </div>
    );
}

function ThreadRightPanel({ thread }) {
    if (!thread) {
        return (
            <div className="flex items-center justify-center py-32 text-sm text-gray-400">
                Select a thread to view checkpoints
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-5 py-3">
                <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                        {thread.session_name ? (
                            <>
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-semibold text-gray-900 truncate">{thread.session_name}</h3>
                                    <Link
                                        to={`/admin/sessions?session_id=${thread.thread_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded transition-colors shrink-0"
                                    >
                                        View Session
                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                    </Link>
                                    <a
                                        href={`/admin-view/${thread.thread_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors shrink-0"
                                    >
                                        View Chat
                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                    </a>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-xs text-gray-400 font-mono truncate">{thread.thread_id}</span>
                                    {thread.user_id && <span className="text-xs text-gray-400 truncate">{thread.user_id}</span>}
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-sm font-semibold text-gray-900 truncate font-mono">{thread.thread_id}</h3>
                                <Link
                                    to={`/admin/sessions?session_id=${thread.thread_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded transition-colors shrink-0"
                                >
                                    View Session
                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                </Link>
                                <a
                                    href={`/admin-view/${thread.thread_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors shrink-0"
                                >
                                    View Chat
                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                </a>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-4">
                        <Badge variant="default">{thread.checkpoint_count} ckpts</Badge>
                        <Badge variant="warning">{thread.write_count} writes</Badge>
                        <Badge variant="purple">{thread.blob_count} blobs</Badge>
                    </div>
                </div>
            </div>
            <div className="flex-1 overflow-hidden">
                <div className="h-full px-5 py-4">
                    <CheckpointViewer threadId={thread.thread_id} />
                </div>
            </div>
        </div>
    );
}

export default function AdminCheckpoints() {
    const [stats, setStats] = useState(null);
    const [statsError, setStatsError] = useState(null);
    const [selectedThread, setSelectedThread] = useState(null);

    useEffect(() => {
        adminApi.getCheckpointStats()
            .then(setStats)
            .catch((err) => setStatsError(err.message || 'Failed to load stats'));
    }, []);

    return (
        <div>
            <div className="mb-4">
                <h1 className="text-lg font-semibold text-gray-900">Checkpoints</h1>
                <p className="text-sm text-gray-500 mt-0.5">LangGraph checkpointer data — debug agent execution flow</p>
            </div>

            {statsError && <Alert variant="error" onClose={() => setStatsError(null)} className="mb-4">{statsError}</Alert>}

            <StatsBar stats={stats} />

            <div className="flex border border-gray-200 rounded-lg bg-white overflow-x-auto" style={{ height: 'calc(100vh - 220px)' }}>
                <div className="w-64 shrink-0 border-r border-gray-200">
                    <ThreadLeftPanel
                        selectedThread={selectedThread}
                        onSelectThread={setSelectedThread}
                    />
                </div>
                <div className="flex-1 overflow-hidden">
                    <ThreadRightPanel thread={selectedThread} />
                </div>
            </div>
        </div>
    );
}
